import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Typography, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '../api/client';
import type { RightCatalogueEntry, RightCategory } from '../api/types';

const categoryColor: Record<RightCategory, 'info' | 'success' | 'warning' | 'error' | 'default'> = {
  Read: 'info',
  Write: 'success',
  Edit: 'warning',
  Delete: 'error',
  Other: 'default',
};

/**
 * Read-only catalogue browser. Rights are auto-discovered from
 * .WithName().RequireRight() decoration on endpoints; this page just renders
 * what the backend has synced. Grouped by FeatureKey for orientation.
 */
export function RightsPage() {
  const [rows, setRows] = useState<RightCatalogueEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get<{ rights: RightCatalogueEntry[] }>('/api/rights/');
      setRows(data?.rights ?? []);
    } catch (ex) {
      setError((ex as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const grouped = useMemo(() => {
    const m = new Map<string, RightCatalogueEntry[]>();
    for (const r of rows) {
      const list = m.get(r.featureKey) ?? [];
      list.push(r);
      m.set(r.featureKey, list);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Rights catalogue</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reload}>Refresh</Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Auto-discovered from <code>.WithName().RequireRight()</code> on each endpoint.
            Keys are <code>{'{feature}.{category}.{action}'}</code>; category is derived
            from the HTTP verb. Read-only — the catalogue is re-synced on every app startup.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && <CircularProgress size={20} />}
        </CardContent>
      </Card>

      {grouped.map(([feature, items]) => (
        <Card key={feature}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {feature} <Chip size="small" label={items.length} sx={{ ml: 1 }} />
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.key} hover>
                    <TableCell><code>{r.key}</code></TableCell>
                    <TableCell>
                      <Chip size="small" label={r.category} color={categoryColor[r.category]} variant="outlined" />
                    </TableCell>
                    <TableCell>{r.description ?? <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.isActive ? 'active' : 'inactive'}
                            color={r.isActive ? 'success' : 'default'} variant={r.isActive ? 'filled' : 'outlined'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
